import type { BaseFieldDef } from "@r8er/components/field/definition";
import { clsx, type ClassValue } from "clsx";
import { camelCase, snakeCase } from "lodash";
import { twMerge } from "tailwind-merge";
import type { Writeable } from "zod";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export interface Address {
  line_1: string;
  line_2: string;
  line_3: string;
  postal_code: string;
  region: string;
  city: string;
  country: string;
}

export const removeEmpty = (obj: object) => {
  const newObj = {};
  Object.keys(obj).forEach((key) => {
    if (obj[key] === Object(obj[key]))
      newObj[key] = removeEmpty(obj[key] as object);
    else if (obj[key] !== undefined) newObj[key] = obj[key];
  });
  return newObj;
};

export const classes = cn;
export const mergeClasses = (...themes: Record<string, string>[]) => {
  return themes.reduce<Record<string, string>>((acc, theme) => {
    const innerTheme = theme
      ? Object.entries(theme).reduce<Record<string, string>>(
          (innerAcc, [key, className]) => {
            return {
              ...innerAcc,
              [key]: innerAcc[key]
                ? classes(className, innerAcc[key])
                : className,
            };
          },
          acc,
        )
      : {};

    return {
      ...acc,
      ...innerTheme,
    };
  }, {});
};

export const cameliseKeys = (obj: Record<string, unknown>) => {
  if (Array.isArray(obj)) {
    return obj.map((v) => cameliseKeys(v));
  } else if (obj != null && obj.constructor === Object) {
    return Object.keys(obj).reduce(
      (result, key) => ({
        ...result,
        [camelCase(key)]: cameliseKeys(obj[key] as Record<string, unknown>),
      }),
      {},
    );
  }
  return obj;
};

export const snakeKeys = (obj: Record<string, unknown>) => {
  if (Array.isArray(obj)) {
    return obj.map((v) => snakeKeys(v));
  } else if (obj != null && obj.constructor === Object) {
    return Object.keys(obj).reduce(
      (result, key) => ({
        ...result,
        [snakeCase(key)]: snakeKeys(obj[key] as Record<string, unknown>),
      }),
      {},
    );
  }
  return obj;
};

export const fromJSONBuffer = (content?: Buffer) =>
  content
    ? typeof content === "string"
      ? content
      : Buffer.from(Object.values(content)).toString()
    : undefined;

export const notNullEmpty = (value: unknown) =>
  undefined !== value &&
  null !== value &&
  "" !== value &&
  (!Array.isArray(value) || value.length !== 0);

export const validate =
  (...rules: BaseFieldDef["validate"][]) =>
  async (record, field, { showError }) => {
    let validated = true;
    if (!notNullEmpty(field.value)) {
      validated = false;
      if (showError) showError(`Field is required`);
    }
    for await (const rule of rules) {
      const ruleValidated =
        typeof rule == "function"
          ? await rule(record, field, { showError })
          : rule;
      if (validated) validated = ruleValidated;
    }
    return validated;
  };

export const validateRequired = (record, field, { showError }) => {
  const validated = notNullEmpty(field.value);
  if (!validated && showError) showError(`Field is required`);
  return validated;
};

export const validateNotZero = (record, field, { showError }) => {
  const validated = notNullEmpty(field.value) && field.value > 0;
  if (!validated && showError)
    showError(`${field.definition.label ?? "Score"} cannot be 0`);
  return validated;
};

export const validateYearRequired = (record, field, { showError }) => {
  const validated = validateRequired(record, field, { showError });
  if (validated) return validateYear(record, field, { showError });
  return validated;
};

export const validateYear = (record, field, { showError }) => {
  const validated =
    null === field.value ||
    undefined === field.value ||
    "" === field.value ||
    field.value?.toString()?.length === 4;
  if (!validated && showError) showError("Year must be four digits");
  return validated;
};

const isValidDate = (d: Date) => !Number.isNaN(d.getMilliseconds());

export const validateDate = (record, field, { showError }) => {
  const validated =
    null === field.value ||
    undefined === field.value ||
    "" === field.value ||
    isValidDate(new Date(field.value));
  if (!validated && showError) showError("Date is not valid");
  return validated;
};

export const writeable = <T>(data: T) => data as Writeable<T>;
